import { useState, useEffect } from "react";
import axios from "axios";
import { X, ArrowLeft, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleReader({ url, onClose }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/proxy/article`, { params: { url } })
      .then((res) => {
        if (res.data.paragraphs?.length > 0 || res.data.description) {
          setArticle(res.data);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [url]);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-white dark:bg-zinc-950 overflow-hidden flex flex-col"
      data-testid="article-reader"
    >
      {/* Top bar */}
      <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 px-4 md:px-8 py-3 flex items-center justify-between bg-white dark:bg-zinc-950">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-sm font-mono text-zinc-600 hover:text-zinc-950 transition-colors"
          data-testid="article-back-btn"
        >
          <ArrowLeft size={16} weight="bold" />
          Back
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-zinc-600 transition-colors"
          data-testid="article-source-link"
        >
          Source
          <ArrowSquareOut size={12} />
        </a>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[720px] mx-auto px-4 md:px-8 py-8">
          {loading ? (
            <div className="py-20 text-center">
              <div className="inline-block w-6 h-6 border-2 border-zinc-300 border-t-zinc-950 rounded-full animate-spin" />
              <p className="text-sm font-mono text-zinc-400 mt-4">Loading article...</p>
            </div>
          ) : error || !article ? (
            <div className="py-20 text-center">
              <p className="text-sm text-zinc-500 mb-4">
                Unable to load article content.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-mono border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
              >
                Read on source site
                <ArrowSquareOut size={14} />
              </a>
            </div>
          ) : (
            <>
              {/* Publisher & Date */}
              {(article.publisher || article.pub_date) && (
                <div className="mb-4 flex items-center gap-3">
                  {article.publisher && (
                    <span className="text-xs font-mono tracking-[0.15em] uppercase text-zinc-500">
                      {article.publisher}
                    </span>
                  )}
                  {article.pub_date && (
                    <span className="text-xs font-mono text-zinc-400">
                      {new Date(article.pub_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>
              )}

              {/* Title */}
              <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-zinc-950 leading-tight mb-6">
                {article.title}
              </h1>

              {/* Description */}
              {article.description && (
                <p className="text-base text-zinc-600 leading-relaxed mb-6 pb-6 border-b border-zinc-200">
                  {article.description}
                </p>
              )}

              {/* Hero image */}
              {article.image && (
                <div className="mb-8">
                  <img
                    src={article.image}
                    alt=""
                    className="w-full h-auto max-h-[400px] object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}

              {/* Paragraphs */}
              <div className="space-y-4">
                {article.paragraphs.map((p, i) => {
                  if (p.type === "h1" || p.type === "h2") {
                    return (
                      <h2
                        key={i}
                        className="text-lg font-medium text-zinc-950 mt-8 mb-2"
                      >
                        {p.text}
                      </h2>
                    );
                  }
                  if (p.type === "h3" || p.type === "h4") {
                    return (
                      <h3
                        key={i}
                        className="text-base font-medium text-zinc-950 mt-6 mb-1"
                      >
                        {p.text}
                      </h3>
                    );
                  }
                  if (p.type === "li") {
                    return (
                      <p key={i} className="text-[15px] text-zinc-700 leading-relaxed pl-4 border-l-2 border-zinc-200">
                        {p.text}
                      </p>
                    );
                  }
                  return (
                    <p key={i} className="text-[15px] text-zinc-700 leading-relaxed">
                      {p.text}
                    </p>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-12 pt-6 border-t border-zinc-200 flex items-center justify-between">
                <button
                  onClick={onClose}
                  className="text-sm font-mono text-zinc-500 hover:text-zinc-950 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={14} />
                  Back to screener
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono text-zinc-400 hover:text-zinc-600 flex items-center gap-1"
                >
                  View original
                  <ArrowSquareOut size={12} />
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
